import { DeliveryTypeCategory } from '@/features/orders/constants/app'
import {
    useFormDeliveryTypeCategory,
    useFormTriggerDeliveryTimeField,
} from '@/features/pos/components/OrderFinalization/form'
import { useDeliveryTypeCategoryHighlight } from '@/features/pos/components/OrderFinalization/parts/FormFields/parts/DeliveryTypeCategorySelector/hooks/useDeliveryTypeCategoryHighlight'
import DeliveryTypeCategoryOption from '@/features/pos/components/OrderFinalization/parts/FormFields/parts/DeliveryTypeCategorySelector/parts/DeliveryTypeCategoryOption'
import { stylesheet } from '@/features/pos/components/OrderFinalization/parts/FormFields/parts/DeliveryTypeCategorySelector/styles'
import { usePosMode } from '@/features/pos/contexts/PosMode'
import { useScreenLayout } from '@/hooks/screen'
import { FC, memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleProp, View, ViewStyle, useWindowDimensions } from 'react-native'
import Animated from 'react-native-reanimated'
import { useStyles } from 'react-native-unistyles'

interface DeliveryTypeCategorySelectorProps {
    deliveryTypeCategories: DeliveryTypeCategory[]
    containerStyle?: StyleProp<ViewStyle>
}

const DeliveryTypeCategorySelector: FC<DeliveryTypeCategorySelectorProps> = ({
    deliveryTypeCategories,
    containerStyle,
}) => {
    const [deliveryTypeCategory, onChange] = useFormDeliveryTypeCategory()

    const triggerDeliveryTimeField = useFormTriggerDeliveryTimeField()

    const mode = usePosMode()
    const layout = useScreenLayout()
    const { width: windowWidth } = useWindowDimensions()
    const layoutVersion = Math.round(windowWidth)

    const isPhone = mode === 'phone'

    const handleChange = (deliveryTypeCategory: DeliveryTypeCategory) => {
        onChange(deliveryTypeCategory)

        if (isPhone) setTimeout(() => triggerDeliveryTimeField())
    }

    const { t } = useTranslation()

    const { styles } = useStyles(stylesheet)

    const activeIndex = useMemo(
        () => deliveryTypeCategories.indexOf(deliveryTypeCategory),
        [deliveryTypeCategories, deliveryTypeCategory]
    )

    const { setItemLayouts, highlightStyle } = useDeliveryTypeCategoryHighlight({
        activeCategory: deliveryTypeCategory,
        activeIndex,
        layoutVersion,
    })

    const labelByCategory = useMemo(
        () => ({
            [DeliveryTypeCategory.Delivery]: t(
                `order.deliveryTypeCategory.${DeliveryTypeCategory.Delivery}`,
                DeliveryTypeCategory.Delivery
            ),
            [DeliveryTypeCategory.EatIn]: t(
                `order.deliveryTypeCategory.${DeliveryTypeCategory.EatIn}`,
                DeliveryTypeCategory.EatIn
            ),
            [DeliveryTypeCategory.Takeaway]: t(
                `order.deliveryTypeCategory.${DeliveryTypeCategory.Takeaway}`,
                DeliveryTypeCategory.Takeaway
            ),
        }),
        [t]
    )

    return (
        <View style={[styles.container, styles.containerLayout(layout), containerStyle]}>
            <Animated.View style={[styles.highlight, highlightStyle]} />
            {deliveryTypeCategories.map((category) => (
                <DeliveryTypeCategoryOption
                    key={`${category}-${layoutVersion}`}
                    category={category}
                    isActive={deliveryTypeCategory === category}
                    disabled={!deliveryTypeCategories.length}
                    label={labelByCategory[category]}
                    onLayout={(event) => {
                        const { x, width } = event.nativeEvent.layout

                        setItemLayouts((prev) => ({
                            ...prev,
                            [category]: { x, width },
                        }))
                    }}
                    onPress={handleChange}
                />
            ))}
        </View>
    )
}

export default memo(DeliveryTypeCategorySelector)
